<template>
  <div class="wrap">
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 03 // Contact</span>
      <h1 class="nerv-heading nerv-heading-xl section-title">Contact</h1>
      <p class="nerv-lede">
        Consulting inquiries and hiring conversations both land in the same inbox.
        Pick the one that fits and it arrives already labelled.
      </p>
    </section>

    <div class="columns">
      <!-- Routed mailto actions. No form, no backend, no third-party script. -->
      <section class="nerv-panel nerv-bracket route-panel">
        <span class="nerv-data-label">Open a channel</span>

        <ul class="routes">
          <li v-for="route in routes" :key="route.label">
            <a :href="mailto(route)" class="route">
              <span class="route-head">
                <span class="nerv-heading nerv-heading-md">{{ route.label }}</span>
                <span class="route-arrow" aria-hidden="true">&#8599;</span>
              </span>
              <span class="nerv-body-sm">{{ route.detail }}</span>
            </a>
          </li>
        </ul>

        <p class="nerv-microcopy note">
          These open your mail client with the subject filled in. If it does not,
          write to
          <a href="mailto:daniil@dev1consulting.com" class="link">daniil@dev1consulting.com</a>
          directly.
        </p>
      </section>

      <!-- Direct -->
      <section class="nerv-panel direct">
        <span class="nerv-data-label">Direct channels</span>
        <dl>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Email</dt>
            <dd class="nerv-datarow-value">
              <a href="mailto:daniil@dev1consulting.com" class="link">daniil@dev1consulting.com</a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Phone</dt>
            <dd class="nerv-datarow-value"><a href="tel:+12149457067" class="link">214-945-7067</a></dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">LinkedIn</dt>
            <dd class="nerv-datarow-value">
              <a href="https://www.linkedin.com/in/DaniilDemidov" target="_blank" rel="noopener" class="link">
                /in/DaniilDemidov
              </a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">GitHub</dt>
            <dd class="nerv-datarow-value">
              <a href="https://github.com/iCodeWebApps" target="_blank" rel="noopener" class="link">
                /iCodeWebApps
              </a>
            </dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Location</dt>
            <dd class="nerv-datarow-value">Plano, Texas (Dallas metro)</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Engagements</dt>
            <dd class="nerv-datarow-value">Project builds, fractional AI lead, 1099 contract</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Start</dt>
            <dd class="nerv-datarow-value">Immediately as 1099 through Dev1 Consulting LLC</dd>
          </div>
        </dl>

        <div class="resume-row">
          <a class="nerv-btn nerv-btn-ghost" href="/Daniil-Demidov-Resume.pdf" download>
            Download resume (PDF)
          </a>
        </div>

        <p class="nerv-microcopy note">
          Dev1 Consulting, LLC. Texas domestic LLC, active and in good standing.
        </p>
      </section>
    </div>

    <!-- Live channel diagnostics, read from the visitor's own browser. -->
    <section class="nerv-section channel">
      <span class="nerv-section-label">Sec 03.1 // Channel status</span>
      <div class="nerv-panel nerv-bracket channel-panel">
        <div class="channel-gauges">
          <NervGauge label="Refresh" :value="t.fps" :max="120" unit=" fps" tone="green" :size="140" />
          <NervGauge
            v-if="t.downlink !== null"
            label="Downlink"
            :value="t.downlink"
            :max="downlinkMax"
            unit=" Mb/s"
            tone="cyan"
            :decimals="1"
            :size="140"
          />
          <NervGauge
            v-if="t.batteryLevel !== null"
            label="Battery"
            :value="t.batteryLevel"
            :max="100"
            unit="%"
            :tone="t.batteryCharging ? 'green' : 'magenta'"
            :size="140"
          />
        </div>
        <dl class="channel-rows">
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Status</dt>
            <dd class="nerv-datarow-value">{{ t.online ? 'Online' : 'Offline' }}</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Effective type</dt>
            <dd class="nerv-datarow-value">{{ t.netType ? t.netType.toUpperCase() : 'Unavailable' }}</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Round trip</dt>
            <dd class="nerv-datarow-value">{{ t.rtt === null ? 'Unavailable' : `${t.rtt} ms` }}</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Time zone</dt>
            <dd class="nerv-datarow-value">{{ t.timezone || 'Unavailable' }}</dd>
          </div>
          <div class="nerv-datarow">
            <dt class="nerv-datarow-key">Session</dt>
            <dd class="nerv-datarow-value">t+ {{ t.uptime }}</dd>
          </div>
        </dl>
        <p class="nerv-microcopy channel-note">
          Live readings from your browser, measured on this device and rendered here.
          Nothing is sent, stored or logged. Values the browser does not expose are
          reported as Unavailable rather than guessed.
        </p>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import NervGauge from '@/components/NervGauge.vue';
import { acquireTelemetry, releaseTelemetry, telemetry } from '@/composables/useTelemetry';

interface Route {
  label: string;
  detail: string;
  subject: string;
  body: string;
}

const ADDRESS = 'daniil@dev1consulting.com';

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Contact',
  components: { NervGauge },
  computed: {
    t() {
      return telemetry;
    },
    downlinkMax(): number {
      const d = telemetry.downlink || 0;
      return d > 10 ? Math.ceil(d / 10) * 10 : 10;
    },
  },
  data() {
    return {
      routes: [
        {
          label: 'Build an LLM or agent product',
          detail:
            'Prompt design, tool calling, retrieval and evals, shipped as a product. Tell me what it has to do and who it has to do it for.',
          subject: 'Project brief: LLM or agent product',
          body: 'What are you trying to build?\n\nWho uses it, and what does it have to be right about?\n\nWhat is already in place?\n\nTimeline and budget range, if you have one:\n',
        },
        {
          label: 'Stand up an AI initiative',
          detail:
            'For teams that have not run one before. Architecture, evaluation, guardrails and the first shipped thing.',
          subject: 'Project brief: AI initiative',
          body: 'What does your team do today?\n\nWhat has been tried so far?\n\nWhat would success look like in ninety days?\n',
        },
        {
          label: 'Platform or integration work',
          detail:
            'Multi-tenant SaaS, enterprise integration, auth and payment flows. The load-bearing parts.',
          subject: 'Project brief: platform work',
          body: 'What system are we talking about?\n\nWhat is breaking, or what needs to exist?\n\nStack and constraints:\n',
        },
        {
          label: 'Hiring for a full-time role',
          detail:
            'Senior or staff applied AI. Resume is on this page and the service record is under Operator.',
          subject: 'Role inquiry',
          body: 'Company and role:\n\nWhat the team is building:\n\nRemote policy and compensation band:\n',
        },
      ] as Route[],
    };
  },
  methods: {
    mailto(route: Route): string {
      const params = new URLSearchParams({ subject: route.subject, body: route.body });
      // URLSearchParams encodes spaces as "+", which mail clients render literally.
      return `mailto:${ADDRESS}?${params.toString().replace(/\+/g, '%20')}`;
    },
  },
  mounted() {
    acquireTelemetry();
  },
  beforeUnmount() {
    releaseTelemetry();
  },
});
</script>

<style scoped>
.section-title {
  margin-block: 0.75rem 1rem;
}

.columns {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: 0.75rem;
  align-items: start;
}

@media (max-width: 900px) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

.routes {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-block: 1.25rem;
  list-style: none;
  background: var(--line-soft);
  border: 1px solid var(--line-soft);
}

.route {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: clamp(0.9rem, 2vw, 1.15rem);
  background: #050505;
  transition: background-color 0.15s linear;
}

.route:hover {
  background: rgba(255, 153, 0, 0.06);
}

.route-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.route-arrow {
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.85rem;
}

.direct dl {
  margin-block-start: 1rem;
}

.resume-row {
  margin-block-start: 1.25rem;
}

.link {
  color: var(--nerv-orange);
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s linear;
}

.link:hover {
  border-bottom-color: var(--nerv-orange);
}

.note {
  margin-block-start: 1.25rem;
  padding-block-start: 1rem;
  border-top: 1px solid var(--line-soft);
  color: var(--text-dim);
}

.channel {
  margin-block-start: 0.75rem;
}

.channel-panel {
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  gap: clamp(1.25rem, 3vw, 2.5rem);
  align-items: start;
  margin-block-start: 0.75rem;
}

@media (max-width: 820px) {
  .channel-panel {
    grid-template-columns: minmax(0, 1fr);
  }
}

.channel-gauges {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
}

.channel-rows .nerv-datarow-value {
  font-variant-numeric: tabular-nums;
}

.channel-note {
  grid-column: 1 / -1;
  padding-block-start: 1rem;
  border-top: 1px solid var(--line-soft);
  color: var(--text-dim);
}
</style>
